// ============================================
// REMINDERS
// ============================================

import { supabase } from '../../lib/supabase';
import type { Reminder } from '../types';
import { requireUserId, throwWithContext, notify } from './_shared';

/* eslint-disable @typescript-eslint/no-explicit-any */

export async function fetchReminders(): Promise<Reminder[]> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('reminders')
    .select('*')
    .eq('user_id', userId);
  if (error) throwWithContext("Couldn't load your reminders", error);
  return (data || []).map(mapReminderFromDb);
}

export async function fetchEnabledReminders(): Promise<Reminder[]> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('reminders')
    .select('*')
    .eq('user_id', userId)
    .eq('is_enabled', true);
  if (error) throwWithContext("Couldn't load your reminders", error);
  return (data || []).map(mapReminderFromDb);
}

export async function addReminder(reminder: Reminder): Promise<void> {
  const userId = await requireUserId();
  const row = mapReminderToDb(reminder, userId);
  const { error } = await supabase.from('reminders').insert(row);
  if (error) throwWithContext("Couldn't create your reminder", error);
  notify('reminders');
}

export async function updateReminder(id: string, updates: Partial<Reminder>): Promise<void> {
  const userId = await requireUserId();
  const dbUpdates = mapReminderToDb(updates);
  delete (dbUpdates as Record<string, unknown>).user_id;
  const { error } = await supabase
    .from('reminders')
    .update(dbUpdates)
    .eq('id', id)
    .eq('user_id', userId);
  if (error) throwWithContext("Couldn't update your reminder", error);
  notify('reminders');
}

function mapReminderFromDb(row: any): Reminder {
  return {
    id: row.id,
    protocolId: row.protocol_id,
    componentId: row.component_id,
    peptideName: row.peptide_name,
    isEnabled: row.is_enabled,
    lastNotified: row.last_notified ?? undefined,
    createdAt: row.created_at,
    phaseId: row.phase_id,
    phaseLabel: row.phase_label ?? undefined,
    phaseNumber: row.phase_number,
    totalPhases: row.total_phases,
    doseMcg: row.dose_mcg,
    doseUnitDisplay: row.dose_unit_display,
    frequency: row.frequency,
    selectedDays: row.selected_days ?? undefined,
    timesOfDay: row.times_of_day ?? [],
    summarySnippet: row.summary_snippet ?? undefined,
    lastDriftCheckedAt: row.last_drift_checked_at ?? undefined,
    snapshotVersion: row.snapshot_version ?? 0,
    mixGroupId: row.mix_group_id ?? null,
  };
}

// Map a (possibly partial) Reminder into a DB row. Only columns whose
// corresponding field is present on the input are written, so partial
// updates from updateReminder(id, { isEnabled: false }) don't NULL out
// schedule/snapshot columns they never touched (P2-05).
function mapReminderToDb(reminder: Partial<Reminder>, userId?: string): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if ('id' in reminder) row.id = reminder.id;
  if ('protocolId' in reminder) row.protocol_id = reminder.protocolId;
  if ('componentId' in reminder) row.component_id = reminder.componentId;
  if ('peptideName' in reminder) row.peptide_name = reminder.peptideName;
  if ('isEnabled' in reminder) row.is_enabled = reminder.isEnabled;
  if ('lastNotified' in reminder) row.last_notified = reminder.lastNotified ?? null;
  if ('createdAt' in reminder) row.created_at = reminder.createdAt;
  if ('phaseId' in reminder) row.phase_id = reminder.phaseId;
  if ('phaseLabel' in reminder) row.phase_label = reminder.phaseLabel ?? null;
  if ('phaseNumber' in reminder) row.phase_number = reminder.phaseNumber;
  if ('totalPhases' in reminder) row.total_phases = reminder.totalPhases;
  if ('doseMcg' in reminder) row.dose_mcg = reminder.doseMcg;
  if ('doseUnitDisplay' in reminder) row.dose_unit_display = reminder.doseUnitDisplay;
  if ('frequency' in reminder) row.frequency = reminder.frequency;
  if ('selectedDays' in reminder) row.selected_days = reminder.selectedDays ?? null;
  if ('timesOfDay' in reminder) row.times_of_day = reminder.timesOfDay;
  if ('summarySnippet' in reminder) row.summary_snippet = reminder.summarySnippet ?? null;
  if ('lastDriftCheckedAt' in reminder) row.last_drift_checked_at = reminder.lastDriftCheckedAt ?? null;
  if ('snapshotVersion' in reminder) row.snapshot_version = reminder.snapshotVersion;
  if ('mixGroupId' in reminder) row.mix_group_id = reminder.mixGroupId ?? null;
  if (userId) row.user_id = userId;
  return row;
}
